// ============================================================================
// contacts/channels.ts — КАНАЛЫ СВЯЗИ контакта (SSoT, чистые функции).
//
// Канал = (вид, источник, значение):
//   internal — чат платформы (доступен всегда);
//   profile  — entity_channels (то, что человек указал о себе);
//   adapter  — adapter_links (РЕАЛЬНАЯ привязка транспорта; только она
//              позволяет мосту доставить сообщение во внешний мессенджер).
//
// Боты (telegram_bot/max_bot) каналом ЧЕЛОВЕКА не являются: у бота нет
// личной переписки платформы, мост людей его не обслуживает.
// ============================================================================

import type { ContactChannel, ContactChannelKind } from './types';

function text(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/** Вид канала из entity_channels (профиль пользователя). */
export function channelKind(raw: string): ContactChannelKind | null {
  const value = String(raw || '').trim().toLowerCase();
  if (value === 'telegram' || value === 'tg') return 'telegram';
  if (value === 'max' || value === 'max_messenger' || value === 'макс') return 'max';
  if (value === 'internal' || value === 'chat') return 'internal';
  return null;
}

export function channelLabel(kind: ContactChannelKind): string {
  return kind === 'telegram' ? 'Telegram' : kind === 'max' ? 'MAX' : 'Внутренний чат';
}

/** Вид адаптера по данным экземпляра (имя/код/иконка) — без догадок по id. */
export function adapterKind(link: Record<string, unknown>): ContactChannelKind | null {
  const haystack = `${text(link.adapterName)} ${text(link.adapterCode)} ${text(link.adapterIcon)}`.toLowerCase();
  // Бот-транспорт — не канал человека (переписка с ботом — отдельный механизм).
  if (haystack.includes('бот') || haystack.includes('bot')) return null;
  if (haystack.includes('telegram') || haystack.includes('телеграм') || haystack.includes('tdlib') || haystack.includes('mtproto')) {
    return 'telegram';
  }
  if (haystack.includes('max') || haystack.includes('макс')) return 'max';
  return null;
}

export function internalChannel(): ContactChannel {
  return {
    kind: 'internal',
    source: 'internal',
    label: channelLabel('internal'),
    value: '',
    verified: true,
  };
}

/** Канал из привязки adapter_links (внешняя идентичность контакта). */
export function adapterChannel(link: Record<string, unknown>): ContactChannel | null {
  const kind = adapterKind(link);
  if (!kind) return null;
  const externalId = text(link.externalId ?? link.external_id);
  const externalName = text(link.externalName ?? link.external_name);
  const value = externalId || externalName;
  if (!value) return null;
  return {
    kind,
    source: 'adapter',
    label: channelLabel(kind),
    value,
    externalName: externalName || undefined,
    verified: true,
    adapterId: text(link.adapterId ?? link.adapter_id),
    adapterName: text(link.adapterName) || text(link.adapterCode),
  };
}

/**
 * Добавить канал без дублей (одна пара «вид + значение» = один канал).
 * Если тот же канал уже есть в виде ПРОИЗВОДНОГО (технический контакт tg_*),
 * данные РЕАЛЬНОЙ привязки adapter_links важнее: подменяем ими.
 */
export function addChannel(list: ContactChannel[], channel: ContactChannel): void {
  const key = `${channel.kind}|${channel.value.toLowerCase()}`;
  const index = list.findIndex((item) => `${item.kind}|${item.value.toLowerCase()}` === key);
  if (index < 0) {
    list.push(channel);
    return;
  }
  const existing = list[index];
  if (!existing.adapterId && channel.adapterId) {
    list[index] = channel;
    return;
  }
  if (!existing.externalName && channel.externalName) {
    list[index] = { ...existing, externalName: channel.externalName };
  }
}

export function mergeChannels(target: ContactChannel[], source: ContactChannel[]): ContactChannel[] {
  for (const channel of source) addChannel(target, channel);
  return target;
}

/** Ключ внешней идентичности: adapter_id + external_id (дедуп контактов). */
export function externalKey(link: Record<string, unknown>): string {
  const adapterId = text(link.adapterId ?? link.adapter_id);
  const externalId = text(link.externalId ?? link.external_id);
  return adapterId && externalId ? `${adapterId}:${externalId}` : '';
}
